// src/helpers/pdfSaldoCliente.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

// Utils
const fmtMoney = n => (Number(n) || 0).toLocaleString("es-AR", { style: "currency", currency: "ARS" });
const fmtDate  = d => (d ? moment(d).format("DD/MM/YYYY") : "");
const sanitize = (s = "") => s.toString().replace(/[\\/:*?"<>|]+/g, "_").trim();
const getNumero = c => c.numero ?? c.codcom ?? c.nrodecomanda ?? "";
const getNroViaje = c => c.nroviaje ?? c.nroViaje ?? c.nro_viaje ?? c.nro ?? "";

function addPageNumbers(doc) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.text(
      `Página ${i} de ${pages}`,
      doc.internal.pageSize.getWidth() - 60,
      doc.internal.pageSize.getHeight() - 12
    );
  }
}

export function generarPDFSaldoCliente({
  clienteNombre = "",
  fechaCorte = new Date(),
  data,
  logoBase64,
  titulo = "INFORME DE SALDOS PENDIENTES",
  empresa = "",
  tipoEntidad = "CLIENTE",
}) {
  const esTercero = tipoEntidad === "TRANSPORTE";
  const doc = new jsPDF({ unit: "pt", format: "A4" });
  const W = doc.internal.pageSize.getWidth();
  const L = 40;
  const R = 40;

  let y = 36;

  if (logoBase64) {
    try { doc.addImage(logoBase64, "PNG", L, y - 20, 90, 36, undefined, "FAST"); } catch {}
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(titulo, L, y + 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
  `${esTercero ? "Tercero" : "Cliente"}: ${clienteNombre || "-"}`, L, y + 32 );
  doc.text(`Fecha de corte: ${fmtDate(fechaCorte)}`, L, y + 46);

  if (empresa) {
    doc.setFont("helvetica", "italic");
    doc.text(empresa, W - R - doc.getTextWidth(empresa), y + 14);
    doc.setFont("helvetica", "normal");
  }

  y += 70;
  doc.setFontSize(12);
  doc.text("Resumen", L, y);

  const totalSaldoComandas = Number(data?.totalSaldoComandas || 0);
  doc.setFontSize(10);
  doc.text(`Saldo por COMANDAS (pendiente): ${fmtMoney(totalSaldoComandas)}`, L, y + 16);

  if (esTercero) {
    const items = data?.comandasConSaldo || [];

    const totalComision = items.reduce(
      (acc, c) => acc + (Number(c.comision) || 0),
      0
    );

    const totalMerma = items.reduce(
      (acc, c) => acc + (Number(c.merma) || 0),
      0
    );

    doc.setTextColor(210, 20, 26);

    doc.text(
      `Total comisiones: ${fmtMoney(totalComision)}`,
      L,
      y + 32
    );

    doc.text(
      `Total merma: ${fmtMoney(totalMerma)}`,
      L,
      y + 46
    );

    doc.setTextColor(0, 0, 0);

    // Dejar espacio para que la tabla no tape las sumatorias.
    y += 34;
  }

  // Comandas con saldo
  const rowsComandas = (data?.comandasConSaldo || []).map(c => ([
    String(getNumero(c)),
    String(getNroViaje(c)),
    fmtDate(c.fecha),
    fmtMoney(c.total),
    fmtMoney(c.aplicado),
    fmtMoney(c.saldo),
  ]));

  console.log("Sample:", (data?.comandasConSaldo || []).slice(0,3)
  .map(x => ({ numero: x.numero, nroviaje: x.nroviaje })));
  
  doc.autoTable({
    startY: y + 28,
    head: [["Número", "Nro Viaje", "Fecha", "Total", "Aplicado", "Saldo"]],
    body: rowsComandas,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [51, 102, 153], halign: "center", valign: "middle" },
    bodyStyles: { valign: "middle" },
    columnStyles: {
      0: { cellWidth: 85 },
      1: { cellWidth: 80,  halign: "center" },
      2: { cellWidth: 80,  halign: "center" },
      3: { cellWidth: 90,  halign: "right" },
      4: { cellWidth: 90,  halign: "right" },
      5: { cellWidth: 90,  halign: "right" },
    },
    foot: [[ "", "", "", "TOTAL", "", fmtMoney(totalSaldoComandas) ]],
    footStyles: { fillColor: [240,240,240], halign: "right", fontStyle: "bold" },
    margin: { left: L, right: R },
    didDrawPage: () => {
      doc.setDrawColor(230);
      doc.line(L, doc.internal.pageSize.getHeight() - 24, W - R, doc.internal.pageSize.getHeight() - 24);
    }
  });

  // Órdenes de Cobro abiertas (informativo)
  let nextY = doc.lastAutoTable?.finalY || (y + 28);
  nextY += 22;

  const rowsOC = (data?.ordenesCobroAbiertas || []).map(oc => ([
    String(oc.numero || ""),
    fmtDate(oc.fecha),
    oc.estado || "",
    fmtMoney(oc.total || 0),
  ]));

  if (rowsOC.length) {
    doc.setFontSize(11);
    doc.text("Órdenes de Cobro abiertas (informativo – no suma al saldo)", L, nextY);
    doc.autoTable({
      startY: nextY + 8,
      head: [["Número", "Fecha", "Estado", "Importe"]],
      body: rowsOC,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [180, 180, 180], halign: "center", valign: "middle" },
      bodyStyles: { valign: "middle" },
      columnStyles: {
        0: { cellWidth: 85 },
        1: { cellWidth: 80, halign: "center" },
        2: { cellWidth: 120, halign: "center" },
        3: { cellWidth: 100, halign: "right" },
      },
      margin: { left: L, right: R },
    });

    nextY = doc.lastAutoTable?.finalY || nextY + 30;
  }

  doc.setFontSize(9);
  doc.setFont("helvetica", "italic");

  const nota = esTercero
    ? "Nota: el saldo pendiente contempla los descuentos que correspondan y los pagos anteriores. Las sumas de comisión y merma corresponden a las comandas exportadas."
    : "Nota: el saldo del cliente se calcula en base a Comandas con saldo > 0 (total – pagos aplicados).";

  const lineasNota = doc.splitTextToSize(nota, W - L - R);

  let notaY = (nextY || (y + 28)) + 20;

  const altoNota = lineasNota.length * 12;
  const limiteInferior = doc.internal.pageSize.getHeight() - 40;

  if (notaY + altoNota > limiteInferior) {
    doc.addPage();
    notaY = 40;
  }

  doc.setTextColor(0, 0, 0);
  doc.text(lineasNota, L, notaY);
  doc.setFont("helvetica", "normal");

  addPageNumbers(doc);

  const nombre = sanitize(clienteNombre || "cliente");
  const fechaNom = moment(fechaCorte).format("YYYY-MM-DD");
  doc.save(`Saldo_${nombre}_${fechaNom}.pdf`);
}
